import type { OpsLoginMethod } from '@nadoutong/contracts';
import type { I18nKey } from '../i18n/index.ts';
import { formatOpsLoginMethods } from './opsUserPickerLabel.ts';

/**
 * Shared display-name fallback chain for a customer's owning manager
 * (#1098): display name → email → ops user id → caller-chosen fallback.
 * Previously copy-pasted in the workspace, ownership, and Customer 360
 * pages (epic #1035 E6-D5 review); the trims mean whitespace-only
 * values fall through like missing ones.
 */
export interface OwnerLabelParts {
  readonly display_name: string | null;
  readonly email: string | null;
  readonly ops_user_id: string | null;
}

export function ownerDisplayLabel(owner: OwnerLabelParts, fallback = '—'): string {
  const displayName = owner.display_name?.trim();
  if (displayName !== undefined && displayName.length > 0) {
    return displayName;
  }
  const email = owner.email?.trim();
  if (email !== undefined && email.length > 0) {
    return email;
  }
  const opsUserId = owner.ops_user_id;
  if (opsUserId !== null && opsUserId.length > 0) {
    return opsUserId;
  }
  return fallback;
}

/**
 * Owner label with the manager's sign-in methods appended (`Andy · Feishu`),
 * so two managers sharing a display name can be told apart. Falls back to
 * `ownerDisplayLabel` alone when methods are unknown or empty.
 */
export function ownerIdentityLabel(
  owner: OwnerLabelParts,
  loginMethods: readonly OpsLoginMethod[] | null | undefined,
  translate: (key: I18nKey) => string,
  fallback = '—',
): string {
  const base = ownerDisplayLabel(owner, fallback);
  if (owner.ops_user_id === null || owner.ops_user_id.length === 0) {
    return base;
  }
  const methods = formatOpsLoginMethods(loginMethods, translate);
  return methods === null ? base : `${base} · ${methods}`;
}
